import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

// Extract org slug from URL if we're inside an /o/:slug/* route, or
// fall back to the slug stored at login time in localStorage.
const useCurrentOrgSlug = () => {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/o\/([^/]+)/);
  if (match) return match[1];
  return localStorage.getItem('eez7_client_orgSlug') || null;
};

const orgPath = (slug, sub) => (slug ? `/o/${slug}${sub}` : sub);

const Navbar = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const orgSlug = useCurrentOrgSlug();

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMobileOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
    if (user?.firstName) return user.firstName;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const initials = () => {
    const name = getUserDisplayName();
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <nav className="eez7-nav">
      <div className="eez7-nav-inner">
        {/* When the user is browsing inside an /o/:slug context (a customer
            membership widget for a specific org), the brand is a plain
            span — no link. Guests of that org shouldn't be able to click
            through to the platform home page. Outside an org context (e.g.
            the platform's own landing), the brand still links home. */}
        {orgSlug ? (
          <span className="eez7-nav-brand" aria-label="EZ11">
            <span className="eez7-nav-logo">ez11</span>
          </span>
        ) : (
          <Link to="/" className="eez7-nav-brand" aria-label="EZ11 home">
            <span className="eez7-nav-logo">ez11</span>
          </Link>
        )}

        <div className="eez7-nav-actions">
          {isLoggedIn && user?.id ? (
            <div className="eez7-user-dropdown" ref={dropdownRef}>
              <button
                className="eez7-user-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
              >
                <span className="eez7-user-avatar">{initials()}</span>
                <span className="eez7-user-name">{getUserDisplayName()}</span>
                <i className={dropdownOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
              </button>
              {dropdownOpen && (
                <div className="eez7-user-menu">
                  <Link className="eez7-menu-item" to={orgPath(orgSlug, '/membership')} onClick={() => setDropdownOpen(false)}>
                    <i className="bi bi-award" /> Membership
                  </Link>
                  <div className="eez7-menu-divider" />
                  <button className="eez7-menu-item eez7-menu-logout" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            orgSlug && (
              <div className="eez7-nav-auth">
                <Link className="eez7-nav-link" to={orgPath(orgSlug, '/login')}>Sign in</Link>
                <Link className="eez7-nav-cta" to={orgPath(orgSlug, '/register')}>Create account</Link>
              </div>
            )
          )}

          <button className="eez7-nav-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            <i className={mobileOpen ? 'bi bi-x-lg' : 'bi bi-list'} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="eez7-nav-drawer">
          {isLoggedIn && user?.id ? (
            <>
              <Link className="eez7-drawer-item" to={orgPath(orgSlug, '/membership')} onClick={() => setMobileOpen(false)}>
                <i className="bi bi-award" /> Membership
              </Link>
              <div className="eez7-drawer-divider" />
              <button className="eez7-drawer-item eez7-drawer-logout" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right" /> Sign out
              </button>
            </>
          ) : (
            orgSlug && (
              <>
                <Link className="eez7-drawer-item" to={orgPath(orgSlug, '/login')} onClick={() => setMobileOpen(false)}>
                  <i className="bi bi-box-arrow-in-right" /> Sign in
                </Link>
                <Link className="eez7-drawer-item eez7-drawer-cta" to={orgPath(orgSlug, '/register')} onClick={() => setMobileOpen(false)}>
                  <i className="bi bi-person-plus" /> Create account
                </Link>
              </>
            )
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
